import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./OncePaywall.module.scss"

// ---------------------------------------------------------------------------
// "Once" (`/once`) — a self-contained NON-ONBOARDING upsell for Private Photo
// Vault. (This run's type was drawn at random → non-onboarding upsell.)
//
// Concept — COMMITMENT / DECIDE-ONCE. Every prior upsell in this repo asks the
// user to *do* something clever (drag a tide, crank a door, pour a vessel, zip a
// bag, tear a ticket…). This one asks them to decide something. The whole pitch
// is the relief of a single good decision: make it once, and every photo and
// video you love is quietly kept safe for a whole year — backed up, locked,
// organized — without you thinking about it again. Yearly = one decision covers
// the year. Monthly = a decision you re-make every month.
//
// Divergence (vs the saturated skeuomorphic-scene family):
//   - Layout:      an ASYMMETRIC EDITORIAL TYPE-POSTER on warm paper — a kinetic
//                  headline is the hero, not a glowing card or a physical object.
//   - Interaction: light. A plan choice + one decisive CTA. No drag mechanic.
//   - Register:    decisive-relief / calm confidence — "one good decision, done."
//   - Art:         light warm-paper editorial. Brand blue is the single accent
//                  (the "line of protection"); NOT a dark vault, glow, or particles.
//   - Value:       commitment + future-self — "decide once, kept safe for a year."
//   - Motion:      KINETIC TYPOGRAPHY. Words mask-rise and settle; a protection
//                  line draws across; your scattered memories snap into a tidy row
//                  and lock in a cascade — one decision protecting all of it.
//
// Honest / App Store compliant: the yearly price is the largest, most prominent
// price for the yearly product; monthly is present but smaller and secondary.
// Savings + trial are computed from the real debug prices. No fake countdown,
// scarcity, or data-loss language — Premium is framed as ongoing protection you
// set once, never as "pay or lose your photos." Renders on built-in debug data;
// sample memories reuse bundled public/byheart/*.jpg (no network egress).
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (file: string) => `${PUB}/byheart/${file}.jpg`

type PlanKey = "yearly" | "monthly"

// A loose spread of memories that snap into a tidy, protected row. Each carries
// its own pre-protection scatter (tilt + drift) so the "settle" reads as one
// motion bringing order to all of them at once.
const MEMORIES = [
  { img: "m02", rot: -7, dx: -10, dy: 8 },
  { img: "m05", rot: 5, dx: 8, dy: -6 },
  { img: "m08", rot: -4, dx: -6, dy: 10 },
  { img: "m11", rot: 6, dx: 10, dy: -4 },
  { img: "m14", rot: -6, dx: -8, dy: 7 },
  { img: "m17", rot: 4, dx: 7, dy: -8 },
]

function monthYear(d: Date) {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" })
}

function OncePaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  useEffect(() => {
    // Blend the safe-area insets into the warm paper so the screen is one sheet.
    actions.setHeaderInsetColor("#faf7f1")
    actions.setFooterInsetColor("#ffffff")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])
  const trialDays = yearlyProduct?.introPricing?.period ?? 0

  // Future-self horizon: the date this one decision keeps you covered through.
  const throughDate = useMemo(() => {
    const now = new Date()
    const d = new Date(now)
    d.setFullYear(now.getFullYear() + 1)
    return monthYear(d)
  }, [])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── the one-decision payoff: scattered → tidy + kept ───────────────────────
  const [protectedNow, setProtectedNow] = useState(reduce)
  const [kept, setKept] = useState(reduce ? MEMORIES.length : 0)
  const timers = useRef<number[]>([])

  useEffect(() => {
    if (reduce) return
    // Let the headline settle first, then draw the line and let the memories
    // fall into place and lock, one after another.
    const t0 = window.setTimeout(() => setProtectedNow(true), 1050)
    timers.current.push(t0)
    MEMORIES.forEach((_, i) => {
      const t = window.setTimeout(
        () => setKept((k) => Math.max(k, i + 1)),
        1350 + i * 150,
      )
      timers.current.push(t)
    })
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t))
      timers.current = []
    }
  }, [reduce])

  const allKept = kept >= MEMORIES.length

  const handleClose = () => native.postNativeEvent("close")
  const buy = useCallback(() => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (p) native.subscribe(p)
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div className={`${styles.page} ${reduce ? styles.noMotion : ""}`}>
      {/* chrome ------------------------------------------------------------- */}
      <header className={styles.top}>
        <span className={styles.brand}>
          Private Photo Vault
          <span className={styles.brandTag}>Premium</span>
        </span>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="14" height="14">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* editorial headline ------------------------------------------------- */}
      <section className={styles.hero}>
        <span className={styles.kicker}>One decision. A whole year.</span>
        <h1 className={styles.head}>
          <span className={styles.line}>
            <span className={styles.rise} style={{ ["--i" as any]: 0 }}>
              Decide
            </span>{" "}
            <span
              className={`${styles.rise} ${styles.accent}`}
              style={{ ["--i" as any]: 1 }}
            >
              once
              <span className={styles.underline} aria-hidden />
            </span>
            <span className={styles.rise} style={{ ["--i" as any]: 1 }}>
              .
            </span>
          </span>
          <span className={styles.line}>
            <span className={styles.rise} style={{ ["--i" as any]: 2 }}>
              Everything you love,
            </span>
          </span>
          <span className={styles.line}>
            <span className={styles.rise} style={{ ["--i" as any]: 3 }}>
              kept safe all year.
            </span>
          </span>
        </h1>
        <p className={styles.sub}>
          Turn on Premium and every photo and video in your vault is quietly
          backed up, locked, and organized — set it once, and it just keeps
          going.
        </p>
      </section>

      {/* the one-decision payoff: your memories fall into a tidy, kept row --- */}
      <section className={styles.proofWrap}>
        <div className={styles.ruleRow}>
          <span
            className={`${styles.rule} ${protectedNow ? styles.ruleOn : ""}`}
          />
          <span
            className={`${styles.keptCount} ${protectedNow ? styles.keptOn : ""}`}
          >
            <span className={styles.keptDot} />
            {allKept ? "all kept" : `${kept} kept`}
          </span>
        </div>

        <div
          className={`${styles.strip} ${protectedNow ? styles.stripOn : ""}`}
          aria-label="Your memories, kept safe"
        >
          {MEMORIES.map((m, i) => {
            const locked = kept > i
            return (
              <div
                key={m.img}
                className={`${styles.mem} ${locked ? styles.memLocked : ""}`}
                style={{
                  ["--rot" as any]: `${m.rot}deg`,
                  ["--dx" as any]: `${m.dx}px`,
                  ["--dy" as any]: `${m.dy}px`,
                  ["--d" as any]: `${i * 55}ms`,
                }}
              >
                <img src={photo(m.img)} alt="" draggable={false} />
                <span className={styles.lockBadge} aria-hidden>
                  <svg viewBox="0 0 24 24" width="11" height="11">
                    <path
                      d="M7 10V8a5 5 0 0 1 10 0v2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.1"
                      strokeLinecap="round"
                    />
                    <rect
                      x="5.4"
                      y="10"
                      width="13.2"
                      height="9.5"
                      rx="2.4"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </div>
            )
          })}
        </div>

        {/* set-once, always-on — three quiet lines that tick on with the row */}
        <ul
          className={`${styles.setOnce} ${protectedNow ? styles.setOnceOn : ""}`}
        >
          {[
            [
              "Auto-backup",
              "set once — every new memory saved in full quality",
            ],
            ["Lock & decoy", "always on, the moment you close the app"],
            ["Albums, sorted", "your library stays tidy for you, not by you"],
          ].map(([t, s], i) => (
            <li key={t} style={{ ["--d" as any]: `${i * 90}ms` }}>
              <span className={styles.tick} aria-hidden>
                <svg viewBox="0 0 24 24" width="12" height="12">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={styles.setText}>
                <b>{t}</b> — {s}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* the offer ---------------------------------------------------------- */}
      <section className={styles.offer}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.on : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.radio} aria-hidden />
          <div className={styles.planYearBody}>
            <div className={styles.planYearTop}>
              <span className={styles.planName}>Yearly</span>
              {savings > 0 && (
                <span className={styles.saveTag}>SAVE {savings}%</span>
              )}
            </div>
            <div className={styles.planYearHorizon}>
              one decision — kept safe through <b>{throughDate}</b>
            </div>
          </div>
          <div className={styles.planYearPrice}>
            <b>{yearlyPrice}</b>
            <span className={styles.per}>/yr</span>
            <span className={styles.perMo}>{yearlyPerMonth}/mo</span>
          </div>
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.on : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.radioSm} aria-hidden />
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthNote}>
            a decision you re-make each month
          </span>
          <span className={styles.planMonthPrice}>
            {monthlyPrice}
            <span className={styles.perSm}>/mo</span>
          </span>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span className={styles.ctaTop}>
            {plan === "yearly"
              ? "Keep them safe for a year"
              : "Turn on Premium"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `${trialDays ? `${trialDays}-day free trial, then ` : ""}${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span aria-hidden>·</span>
          <span className={styles.legalNote}>Sample memories shown</span>
        </div>
      </section>
    </div>
  )
}

export default OncePaywall
